import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import TopBar from "../../components/TopBar";
import { Field } from "../Fields";
import { api, fmtUSD, todayISO } from "../../lib/api";
import { Save, Plus, Trash2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const LINE_TYPES = [
  { value: "labor", label: "Labor (hours × rate)" },
  { value: "parts", label: "Parts (qty × cost)" },
  { value: "load", label: "Load (miles × rate)" },
  { value: "item", label: "Item / Flat" },
];

const emptyLine = (type = "item") => ({ type, description: "", quantity: 1, rate: 0, amount: 0 });

export default function InvoiceForm({ businessId, returnPath }) {
  const navigate = useNavigate();
  const { invoiceId } = useParams();
  const isEdit = invoiceId && invoiceId !== "new";

  const [customers, setCustomers] = useState([]);
  const [parts, setParts] = useState([]);
  const [form, setForm] = useState({
    business_id: businessId,
    customer_id: "",
    issue_date: todayISO(),
    due_date: "",
    status: "draft",
    lines: [emptyLine("labor")],
    tax_pct: 0,
    notes: "",
    is_internal: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get(`/parts?business_id=${businessId}`).then((r) => setParts(r.data)).catch(() => {});
    if (isEdit) {
      api.get(`/invoices/${invoiceId}`).then((r) => {
        setForm({
          business_id: r.data.business_id,
          customer_id: r.data.customer_id,
          issue_date: r.data.issue_date,
          due_date: r.data.due_date || "",
          status: r.data.status,
          lines: r.data.lines.length ? r.data.lines : [emptyLine("labor")],
          tax_pct: r.data.tax_pct,
          notes: r.data.notes || "",
          is_internal: !!r.data.is_internal,
        });
      });
    }
  }, [invoiceId, isEdit, businessId]);

  const updateLine = (i, patch) => setForm({
    ...form,
    lines: form.lines.map((ln, idx) => idx === i ? { ...ln, ...patch } : ln),
  });
  const addLine = (type) => setForm({ ...form, lines: [...form.lines, emptyLine(type)] });
  const removeLine = (i) => setForm({ ...form, lines: form.lines.filter((_, idx) => idx !== i) });

  const lineAmount = (ln) => (parseFloat(ln.quantity) || 0) * (parseFloat(ln.rate) || 0);
  const subtotal = form.lines.reduce((s, ln) => s + lineAmount(ln), 0);
  const taxAmount = subtotal * ((parseFloat(form.tax_pct) || 0) / 100);
  const total = subtotal + taxAmount;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_id) { toast.error("Pick a customer"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        tax_pct: parseFloat(form.tax_pct) || 0,
        lines: form.lines.map((ln) => ({
          ...ln,
          quantity: parseFloat(ln.quantity) || 0,
          rate: parseFloat(ln.rate) || 0,
        })),
      };
      let saved;
      if (isEdit) {
        saved = (await api.put(`/invoices/${invoiceId}`, payload)).data;
      } else {
        saved = (await api.post(`/invoices`, payload)).data;
      }
      toast.success(`Invoice ${saved.invoice_number} saved`);
      navigate(returnPath || "..");
    } catch { toast.error("Could not save invoice"); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <TopBar
        title={isEdit ? "Edit Invoice" : "New Invoice"}
        subtitle="Mix line types freely — labor, parts, load, and flat items can all go on one invoice."
        actions={
          <div className="flex flex-wrap items-center gap-2 mt-6">
            <button data-testid="back-btn" onClick={() => navigate(returnPath || "..")} className="border border-[#1F1E1B] px-4 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#1F1E1B] hover:text-white flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> Back</button>
            <button data-testid="save-invoice-btn" onClick={onSubmit} disabled={saving} className="bg-[#1A4135] text-white px-5 py-2 font-bold uppercase tracking-wider text-sm hover:bg-[#133128] flex items-center gap-2 disabled:opacity-50"><Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Invoice"}</button>
          </div>
        }
      />

      <form onSubmit={onSubmit} className="space-y-6" data-testid="invoice-form">
        <label className="flex items-start gap-2 bg-[#FAF7EC] border-2 border-[#1F1E1B] p-3 cursor-pointer" data-testid="invoice-internal-toggle">
          <input
            type="checkbox"
            data-testid="invoice-internal-checkbox"
            checked={!!form.is_internal}
            onChange={(e) => {
              const checked = e.target.checked;
              setForm({
                ...form,
                is_internal: checked,
                customer_id: checked ? `internal_farm_${businessId}` : "",
              });
            }}
            className="mt-1"
          />
          <div>
            <div className="text-xs font-bold uppercase tracking-wider">Internal invoice — billed to the Farm</div>
            <div className="text-[11px] text-[#66645E] mt-0.5">
              Marks this as cross-business work. On save, a matching Farm expense is automatically
              created (and updated/deleted in lockstep with this invoice) so the Farm books reflect
              the cost of work done internally by {businessId === "jk_repair" ? "J&K Repair" : "J&K Trucking"}.
            </div>
          </div>
        </label>

        <div className="bg-white border border-[#1F1E1B] p-5 grid grid-cols-1 md:grid-cols-4 gap-4">
          <Field label="Customer" required>
            <select data-testid="invoice-customer-select" required disabled={!!form.is_internal} value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} className={`w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm ${form.is_internal ? "opacity-60" : ""}`}>
              <option value="">— Select customer —</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.company ? `${c.name} (${c.company})` : c.name}</option>)}
            </select>
          </Field>
          <Field label="Issue Date" required>
            <input data-testid="invoice-issue-date" type="date" required value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
          </Field>
          <Field label="Due Date">
            <input data-testid="invoice-due-date" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
          </Field>
          <Field label="Status">
            <select data-testid="invoice-status-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm">
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
              <option value="void">Void</option>
            </select>
          </Field>
        </div>

        <div>
          <div className="flex items-baseline justify-between border-b border-[#1F1E1B] pb-2 mb-3">
            <h3 className="font-display text-lg font-bold uppercase tracking-wider">Line Items</h3>
            <div className="flex gap-2">
              {LINE_TYPES.map((t) => (
                <button key={t.value} type="button" data-testid={`add-line-${t.value}`} onClick={() => addLine(t.value)} className="text-xs font-bold uppercase tracking-wider border border-[#1F1E1B] px-3 py-1 hover:bg-[#1F1E1B] hover:text-white flex items-center gap-1">
                  <Plus className="w-3 h-3" /> {t.value}
                </button>
              ))}
            </div>
          </div>
          <div className="bg-white border border-[#1F1E1B] overflow-x-auto">
            <table className="ledger-table" data-testid="invoice-lines-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Description</th>
                  <th className="num">Qty / Hrs / Miles</th>
                  <th className="num">Rate</th>
                  <th className="num">Amount</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {form.lines.map((ln, i) => (
                  <tr key={i} data-testid={`invoice-line-${i}`}>
                    <td>
                      <select data-testid={`line-type-${i}`} value={ln.type} onChange={(e) => updateLine(i, { type: e.target.value })} className="bg-transparent border-b border-dashed border-[#D2CEC1] text-xs uppercase focus:outline-none">
                        {LINE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.value}</option>)}
                      </select>
                    </td>
                    <td>
                      <input data-testid={`line-desc-${i}`} value={ln.description} onChange={(e) => updateLine(i, { description: e.target.value })} placeholder={ln.type === "load" ? "Pickup → Delivery" : ""} className="w-full bg-transparent border-b border-dashed border-[#D2CEC1] focus:outline-none focus:border-[#1F1E1B]" />
                      {ln.type === "parts" && parts.length > 0 && (
                        <select
                          data-testid={`line-part-picker-${i}`}
                          value={ln.part_id || ""}
                          onChange={(e) => {
                            const part = parts.find((p) => p.id === e.target.value);
                            if (part) updateLine(i, { part_id: part.id, description: part.name, rate: part.sell_price, unit_cost: part.cost });
                            else updateLine(i, { part_id: "", unit_cost: 0 });
                          }}
                          className="mt-1 w-full bg-[#FAF7EC] border border-dashed border-[#1F1E1B] px-2 py-1 text-[11px]"
                        >
                          <option value="">📦 Pick from catalog…</option>
                          {parts.map((p) => <option key={p.id} value={p.id}>{p.name} — {fmtUSD(p.sell_price)} (profit {fmtUSD(p.sell_price - p.cost)})</option>)}
                        </select>
                      )}
                    </td>
                    <td><input data-testid={`line-qty-${i}`} type="number" step="0.01" value={ln.quantity} onChange={(e) => updateLine(i, { quantity: e.target.value })} className="w-28 text-right bg-transparent border-b border-dashed border-[#D2CEC1] focus:outline-none focus:border-[#1F1E1B] tabular" /></td>
                    <td><input data-testid={`line-rate-${i}`} type="number" step="0.01" value={ln.rate} onChange={(e) => updateLine(i, { rate: e.target.value })} className="w-28 text-right bg-transparent border-b border-dashed border-[#D2CEC1] focus:outline-none focus:border-[#1F1E1B] tabular" /></td>
                    <td className="num font-bold">{fmtUSD(lineAmount(ln))}</td>
                    <td><button type="button" data-testid={`remove-line-${i}`} onClick={() => removeLine(i)} className="p-1 border border-[#BC4727] text-[#BC4727] hover:bg-[#BC4727] hover:text-white"><Trash2 className="w-3 h-3" /></button></td>
                  </tr>
                ))}
                <tr className="bg-[#F5F4F0]">
                  <td colSpan={4} className="text-right font-bold uppercase tracking-wider text-xs">Subtotal</td>
                  <td className="num font-bold" data-testid="invoice-subtotal">{fmtUSD(subtotal)}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white border border-[#1F1E1B] p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Tax %">
            <input data-testid="invoice-tax-pct" type="number" step="0.01" value={form.tax_pct} onChange={(e) => setForm({ ...form, tax_pct: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
          </Field>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">Tax Amount</div>
            <div className="font-display text-2xl font-black tabular mt-1">{fmtUSD(taxAmount)}</div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">Total</div>
            <div className="font-display text-3xl font-black tabular mt-1 text-[#1A4135]" data-testid="invoice-total">{fmtUSD(total)}</div>
          </div>
        </div>

        <Field label="Notes">
          <textarea data-testid="invoice-notes-input" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Payment terms, thank-you message, anything for the customer" className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
        </Field>
      </form>
    </div>
  );
}
